'use client';

import { useEffect, useRef, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { isBingVariant, initializeMicrosoftPixelTracking } from 'utils';

/**
 * MicrosoftPixelTrackerContent - Internal component that uses useSearchParams
 *
 * Initializes Microsoft Pixel (Bing UET) tracking for Bing campaign variants.
 *
 * **How it works:**
 * 1. Reads `p_ref` URL parameter from search params
 * 2. Checks if p_ref indicates a Bing campaign variant
 * 3. Initializes Microsoft Pixel tracking (only once per session)
 *
 * **Based on Gatsby implementation:**
 * packages/core/src/templates/DownloadSoftware.tsx (handleBingPrefTracking)
 */
function MicrosoftPixelTrackerContent() {
  const searchParams = useSearchParams();
  const initialized = useRef(false);

  useEffect(() => {
    // Only initialize once per component lifetime
    if (initialized.current) {
      return;
    }

    const pref = searchParams.get('p_ref');

    // Check if this is a Bing campaign variant
    if (isBingVariant(pref)) {
      initializeMicrosoftPixelTracking();
      initialized.current = true;
    }
  }, [searchParams]);

  return null; // This component doesn't render anything
}

/**
 * MicrosoftPixelTracker - Client Component with Suspense boundary
 *
 * Wraps the content component with Suspense to handle async searchParams.
 * This component should be placed on pages that receive Bing campaign traffic.
 *
 * @example
 * // In page.tsx
 * export default function DownloadPage() {
 *   return (
 *     <>
 *       <MicrosoftPixelTracker />
 *       {/* page content *\/}
 *     </>
 *   );
 * }
 */
export function MicrosoftPixelTracker() {
  return (
    <Suspense fallback={null}>
      <MicrosoftPixelTrackerContent />
    </Suspense>
  );
}
